import React, { useEffect, useState } from 'react';
import { getManualReviews, getSubscriptionStatus } from '../services/api';
import styles from './Stats.module.scss';
import {
  BarChart, Bar, XAxis, YAxis,
  ResponsiveContainer, Legend, LineChart, Line, CartesianGrid, Tooltip
} from 'recharts';

interface Review {
  id: string;
  author: string;
  content: string;
  rating: number;
  date?: string; // ISO "YYYY-MM-DDTHH:mm:ssZ"
}

function normalizePlan(p?: string | null): 'free' | 'basic' | 'pro' {
  const v = (p || '').toLowerCase();
  if (v.includes('pro')) return 'pro';
  if (v.includes('basic')) return 'basic';
  if (v.includes('free')) return 'free';
  return 'basic';
}

const Stats: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<'free' | 'basic' | 'pro'>('basic');

  useEffect(() => {
    const load = async () => {
      try {
        const subResp = await getSubscriptionStatus();
        const plan = normalizePlan(subResp.subscription?.plan || null);
        setCurrentPlan(plan);

        const data = await getManualReviews();
        setReviews(Array.isArray(data) ? data : []);
      } catch {
        setError('Erreur lors du chargement des statistiques.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div>Chargement des statistiques...</div>;
  if (error) return <div>{error}</div>;

  // ---------- Core metrics ----------
  const total = reviews.length;
  const sum = reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0);
  const avg = total ? sum / total : 0;
  const avgRating = avg.toFixed(2);

  // Correct star distribution
  const buckets: Record<1 | 2 | 3 | 4 | 5, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const r of reviews) {
    const s = Math.max(1, Math.min(5, Math.round(Number(r.rating))));
    buckets[s as 1 | 2 | 3 | 4 | 5]++;
  }
  const distributionRows = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: buckets[star as 1 | 2 | 3 | 4 | 5],
    pct: total ? (buckets[star as 1 | 2 | 3 | 4 | 5] / total) * 100 : 0
  }));

  // Reputation score: scale average to 100 (5★ -> 100, 1★ -> 20)
  const reputationScore = total ? Math.round((avg / 5) * 100) : 0;

  // ---------- Trends ----------
  const monthCount: Record<string, number> = {};
  const monthAgg: Record<string, { sum: number; count: number }> = {};
  for (const r of reviews) {
    if (!r.date) continue;
    const month = r.date.slice(0, 7); // YYYY-MM
    monthCount[month] = (monthCount[month] || 0) + 1;
    if (!monthAgg[month]) monthAgg[month] = { sum: 0, count: 0 };
    monthAgg[month].sum += Number(r.rating || 0);
    monthAgg[month].count += 1;
  }
  const months = Object.keys(monthCount).sort(); // chronological display

  const monthTrend = months.map(m => ({ month: m, count: monthCount[m] }));
  const monthRatingTrend = months.map(m => ({
    month: m,
    avg: monthAgg[m].count ? Number((monthAgg[m].sum / monthAgg[m].count).toFixed(2)) : 0
  }));

  // (No real response-time data in Review type; show N/A rather than random)
  const avgResponseTime = '—';

  // Top/Bottom Reviews
  const sorted = [...reviews].sort((a, b) => b.rating - a.rating);
  const topReviews = sorted.slice(0, 3);
  const bottomReviews = [...reviews].sort((a, b) => a.rating - b.rating).slice(0, 3);

  return (
    <main className={styles.statsContainer}>
      <h1>Statistiques</h1>

      <div className={styles.statsCards}>
        <div className={styles.statsCard}>
          <strong className={styles.statsCardTitle}>Nombre d'avis</strong>
          <div className={styles.statsCardValue}>{total}</div>
        </div>
        <div className={styles.statsCard}>
          <strong className={styles.statsCardTitle}>Note moyenne</strong>
          <div className={`${styles.statsCardValue} ${styles.statsCardValueStar}`}>★ {avgRating}</div>
        </div>
        <div className={styles.statsCard}>
          <strong className={styles.statsCardTitle}>Temps moyen de réponse</strong>
          <div className={styles.statsCardValue}>{avgResponseTime}</div>
        </div>
        <div className={styles.statsCard}>
          <strong className={styles.statsCardTitle}>Score de réputation</strong>
          <div className={styles.statsCardValue}>{reputationScore}/100</div>
        </div>
      </div>

      <div className={styles.statsBarChart}>
        <strong>Répartition des notes</strong>
        <div className={styles.statsBars}>
          {distributionRows.map(row => (
            <div key={row.star} className={styles.statsBarRow}>
              <span className={styles.statsBarLabel}>{row.star}★</span>
              <div className={styles.statsBarBg}>
                <div className={styles.statsBar} style={{ width: `${row.pct}%` }} />
              </div>
              <span className={styles.statsBarCount}>{row.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.statsBarChart}>
        <strong>Tendance des avis par mois</strong>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={monthTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#2563eb" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.statsBarChart}>
        <strong>Note moyenne par mois</strong>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={monthRatingTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis domain={[1, 5]} />
            <Tooltip />
            <Legend />
            <Line dataKey="avg" stroke="#f59e42" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Advanced charts: PRO only (keep; reputation already fixed above) */}
      {currentPlan === 'pro' && (
        <>
          {/* You can wire a real response-time series when you store respondedAt */}
          <div className={styles.statsBarChart}>
            <strong>Temps moyen de réponse par mois</strong>
            <div className={styles.statsEmptyHint}>Données non disponibles</div>
          </div>

          <div className={styles.statsBarChart}>
            <strong>Score de réputation par mois</strong>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={months.map(m => ({
                  month: m,
                  score: monthAgg[m].count
                    ? Math.round(((monthAgg[m].sum / monthAgg[m].count) / 5) * 100)
                    : 0
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line dataKey="score" stroke="#388e3c" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      <div className={styles.statsBarChart}>
        <strong>Top 3 avis</strong>
        <ul>
          {topReviews.map(r => (
            <li key={r.id}><span style={{ color: '#388e3c' }}>★ {r.rating}</span> — <strong>{r.author}</strong>: {r.content}</li>
          ))}
        </ul>
        <strong>Bottom 3 avis</strong>
        <ul>
          {bottomReviews.map(r => (
            <li key={r.id}><span style={{ color: '#d32f2f' }}>★ {r.rating}</span> — <strong>{r.author}</strong>: {r.content}</li>
          ))}
        </ul>
      </div>
    </main>
  );
};

export default Stats;
